// Layout calculation utilities for Meeting components

/**
 * Calculate padding styles for overlays and banners
 * @param {string} meetingOverlay - Meeting overlay URL
 * @param {string} bannerName - Banner name
 * @returns {Object} Padding styles
 */
export const calculatePaddingStyles = (meetingOverlay, bannerName) => {
  const hasOverlayOrBanner = meetingOverlay || bannerName;

  if (!hasOverlayOrBanner) {
    return {
      paddingLeft: 0,
      paddingRight: 0,
    };
  }

  return {
    paddingLeft: 0,
    paddingRight: 0,
  };
};

/**
 * Calculate sidebar offset based on localStorage width
 * @param {boolean} isGuest - Is guest user
 * @returns {number} Sidebar offset value
 */
export const calculateSidebarOffset = (isGuest = false) => {
  if (isGuest) return 0;
  const sidebarWidth = Number(localStorage.getItem('ProSidebarWidth')) || 80;
  return sidebarWidth * 2.5;
};

/**
 * Calculate horizontal padding for main container
 * @param {number} mainContainerHorizontalPadding - Base padding
 * @param {boolean} mainScreenViewActive - Is main screen active
 * @param {Array} singleRow - Single row participants
 * @param {string} sideBarMode - Sidebar mode
 * @returns {string} Calculated padding in vh
 */
export const calculateHorizontalPadding = (
  mainContainerHorizontalPadding,
  mainScreenViewActive,
  singleRow,
  sideBarMode
) => {
  const shouldAddExtraPadding = 
    !mainScreenViewActive &&
    singleRow.length > 12 &&
    singleRow.length < 17 &&
    typeof sideBarMode !== "string";

  if (mainContainerHorizontalPadding === 0) {
    return shouldAddExtraPadding ? '16vh' : '0vh';
  }

  const extraPadding = shouldAddExtraPadding ? '16vh' : '0';
  return `calc(${pxToVh(mainContainerHorizontalPadding)} + ${extraPadding})`;
};

/**
 * Calculate inner width for containers
 * @param {Object} params - Calculation parameters
 * @returns {number} Calculated width
 */
export const calculateInnerWidth = ({
  width,
  spacing,
  mobilePortrait,
  spotlightParticipantId,
  sideBarPinnedParticipantIds,
  presentingSideBarWidth,
  sidebarOffset,
  singleRow,
  mainContainerHorizontalPadding,
  sideBarType
}) => {
  if (mobilePortrait) {
    return width - 2 * spacing;
  }

  if (!spotlightParticipantId) {
    return 0;
  }

  let baseWidth = width;
  if (sideBarPinnedParticipantIds?.length > 0) {
    baseWidth = width - presentingSideBarWidth - sidebarOffset;
  }
  baseWidth -= 2 * spacing;

  if (singleRow.length === 0) {
    const sideBarAdjustment = sideBarType === true ? spacing * 9 : -spacing * 2;
    baseWidth -= (2 * mainContainerHorizontalPadding) + sideBarAdjustment;
  }

  return baseWidth;
};

/**
 * Calculate whiteboard dimensions
 * @param {Object} params - Dimension parameters
 * @returns {Object} Whiteboard dimensions
 */
export const calculateWhiteboardDimensions = ({
  width,
  height,
  spacing,
  isMobile,
  presentingSideBarWidth,
  whiteboardToolbarWidth,
  whiteboardSpacing,
  whiteboardStarted,
  convertHWAspectRatio
}) => {
  const whiteboardWidth = width -
    (isMobile ? 0 : presentingSideBarWidth) -
    2 * spacing -
    (whiteboardToolbarWidth + 2 * whiteboardSpacing) -
    (whiteboardToolbarWidth === 0 ? 2 * 16 : 0);

  const dims = convertHWAspectRatio({
    height: height - 2 * spacing - (whiteboardToolbarWidth === 0 ? 2 * 16 : 0),
    width: whiteboardStarted ? whiteboardWidth : 0,
  });

  return {
    ...dims,
    whiteboardToolbarWidth,
    whiteboardSpacing,
    originalHeight: height,
    originalWidth: width - (isMobile ? 0 : presentingSideBarWidth) - 2 * spacing,
  };
};

/**
 * Get quality setting based on meeting resolution
 * @param {string} meetingResolution - Meeting resolution setting
 * @param {Object} meetingResolutions - Resolution constants
 * @returns {string} Quality setting
 */
export const getQualitySetting = (meetingResolution, meetingResolutions) => {
  if (!meetingResolution) return "s2t2";
  if (meetingResolution === meetingResolutions.SD) return "s1t2";
  if (meetingResolution === meetingResolutions.HD) return "s2t2";
  return "s2t2";
};

/**
 * Calculate flex value for spotlight container
 * @param {string} spotlightParticipantId - Spotlight participant ID
 * @param {Array} sideBarPinnedParticipantIds - Pinned participant IDs
 * @returns {string} Flex value
 */
export const calculateSpotlightFlex = (spotlightParticipantId, sideBarPinnedParticipantIds) => {
  if (spotlightParticipantId && sideBarPinnedParticipantIds?.length > 0) {
    return '1 1 0%';
  }
  if (spotlightParticipantId) {
    return '1 1 auto';
  }
  return '0 0 auto';
};

/**
 * Calculate flex value for sidebar container
 * @param {string} spotlightParticipantId - Spotlight participant ID
 * @param {Array} sideBarPinnedParticipantIds - Pinned participant IDs
 * @param {number} presentingSideBarWidth - Sidebar width
 * @returns {string} Flex value
 */
export const calculateSidebarFlex = (spotlightParticipantId, sideBarPinnedParticipantIds, presentingSideBarWidth) => {
  if (spotlightParticipantId && sideBarPinnedParticipantIds?.length > 0) {
    return `0 0 ${presentingSideBarWidth}px`;
  }
  return '1 1 auto';
};

/**
 * Helper function to convert pixel values to vh
 * @param {number} px - Pixel value
 * @returns {string} vh value
 */
export const pxToVh = (px) => `${(px / 10).toFixed(1)}vh`;

/**
 * Helper function to convert pixel values to vw
 * @param {number} px - Pixel value
 * @returns {string} vw value
 */
export const pxToVw = (px) => `${(px / 10).toFixed(1)}vw`;

/**
 * Helper function to convert numeric spacing to vh for use in calc()
 * @param {number} spacing - Spacing value
 * @returns {string} vh value
 */
export const spacingToVh = (spacing) => `${(spacing / 10).toFixed(1)}vh`;
